import type {
  DatePickerProps as AriaDatePickerProps,
  DateValue,
  ValidationResult,
} from "react-aria-components";
import {
  I18nProvider,
  DateInput,
  DatePicker,
  DatePickerStateContext,
  DateSegment,
  Group,
  Dialog,
} from "react-aria-components";
import type { ReactNode } from "react";
import { tv } from "tailwind-variants";
import { useCallback, useContext } from "react";

import { Calendar2 } from "@/components/UI2/molecules/Calendar";
import CalendarIcon from "@/assets/images/icons2/icon--calendar.svg?component";
import { cn } from "@/lib/helpers/cn";
import dayjs from "@/lib/helpers/dayjs";
import { FieldError2 } from "@/components/UI2/atoms/FieldError";
import { getCalendarDateFromDayjs } from "@/lib/helpers/getCalendarDateFromDayjs";
import { Label2 } from "@/components/UI2/atoms/Label";
import { Text2 } from "@/components/UI2/atoms/Text";

import { Button2 } from "../atoms/Button";
import { Popover2 } from "../atoms/Popover";

export type DatePicker2Props<T extends DateValue> = {
  description?: string;
  errorMessage?: string | ((validation: ValidationResult) => ReactNode);
  label?: string;
} & AriaDatePickerProps<T>;

export function DatePicker2<T extends DateValue>({
  className,
  description,
  errorMessage,
  label,
  ...props
}: DatePicker2Props<T>) {
  const { dateInput, dateSegment, group, root } = datePickerStyles();

  return (
    <I18nProvider locale="en-US">
      <DatePicker className={cn(root(), className)} {...props}>
        {label && (
          <Label2 isInvalid={props.isInvalid} isRequired={props.isRequired} label={label} />
        )}

        <Group className={cn(group())}>
          <DateInput className={dateInput()}>
            {(segment) => <DateSegment className={dateSegment()} segment={segment} />}
          </DateInput>

          <Button2
            className="tw:mr-1.5! tw:aspect-square tw:p-1! tw:text-text-tertiary"
            variant="blank"
          >
            <CalendarIcon className="tw:block tw:size-4.5" />
          </Button2>
        </Group>

        {description && !props.isInvalid && <Text2 slot="description" text={description} />}

        <FieldError2>{errorMessage}</FieldError2>

        <Popover2>
          <Dialog aria-label="Calendar">
            <Calendar2>
              <DatePickerFooter />
            </Calendar2>
          </Dialog>
        </Popover2>
      </DatePicker>
    </I18nProvider>
  );
}

function DatePickerFooter() {
  const state = useContext(DatePickerStateContext);

  const hasValue = !!state?.value;

  const onClear = useCallback(() => {
    state?.setValue(null);
    state?.setOpen(false);
  }, [state]);

  const onToday = useCallback(() => {
    const today = dayjs();
    const todayCalendarDate = getCalendarDateFromDayjs(today);

    state?.setValue(todayCalendarDate);
    state?.setOpen(false);
  }, [state]);

  return (
    <>
      <Button2 isDisabled={!hasValue} onPress={onClear} size="xs" variant="secondary">
        Clear
      </Button2>
      <Button2 onPress={onToday} size="xs" variant="secondary">
        Today
      </Button2>
    </>
  );
}

const datePickerStyles = tv({
  slots: {
    dateInput:
      "tw:flex tw:flex-1 tw:items-center tw:gap-0.5 tw:px-2.5 tw:py-2 tw:text-sm tw:cursor-text",
    dateSegment: cn(
      "tw:rounded tw:px-0.5 tw:text-sm tw:text-text-secondary",
      "atom-focus",
      "tw:caret-red tw:tabular-nums",
      "tw:placeholder:text-text-quinary",
      "tw:[data-type=literal]:px-0 tw:[data-type=literal]:text-text-tertiary",
      "tw:[data-focused]:text-text-primary",
    ),
    group: cn(
      "atom-focus-within",
      "tw:focus-within:border-stroke-bright-purple-primary",
      "tw:flex tw:h-11 tw:w-full tw:items-center",
      "tw:rounded-sm tw:border tw:border-solid tw:border-stroke-tertiary tw:bg-fill-white",
      "tw:transition-all tw:duration-150",
      "tw:hover:border-stroke-secondary",
      "tw:disabled:cursor-not-allowed tw:disabled:border-stroke-tertiary tw:disabled:bg-fill-senary",
      "tw:data-invalid:border-stroke-error-primary",
    ),
    root: "tw:flex tw:flex-col tw:gap-1.5 tw:w-full",
  },
});
